import React from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from '@hausgold/designsystem';

const STAR_PATH =
  'M12 .587l3.668 7.431 8.2 1.192-5.934 5.784 1.401 8.168L12 18.896l-7.335 3.866 1.401-8.168L.132 9.21l8.2-1.192z';

/*
 * A single star; `fill` is any CSS colour. `display:block` drops the inline
 * descender gap so the row height is exactly 16px and aligns with the text.
 */
const Star = ({ fill }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    aria-hidden
    focusable="false"
    style={{ display: 'block' }}
  >
    <path d={STAR_PATH} fill={fill} />
  </svg>
);

Star.propTypes = { fill: PropTypes.string.isRequired };

/**
 * Renders a row of five stars filled proportionally to `value` (0–5) by
 * overlaying a gold star row, clipped to the exact percentage, on a grey one.
 *
 * The gold row lives in an inner `max-content` wrapper so its stars keep their
 * full size; the outer box constrains the width and clips with `overflow`.
 * (A flex row constrained directly to the percentage would shrink its stars
 * instead of clipping them, making the gold stars narrower than the grey ones.)
 */
const Stars = ({ value }) => {
  const pct = `${(Math.max(0, Math.min(5, value)) / 5) * 100}%`;
  return (
    <Box position="relative" display="inline-flex" lineHeight="0">
      <Flex>
        {[0, 1, 2, 3, 4].map((i) => (
          <Star key={i} fill="#DADCE0" />
        ))}
      </Flex>
      <Box position="absolute" top="0" left="0" width={pct} overflow="hidden">
        <Flex width="max-content">
          {[0, 1, 2, 3, 4].map((i) => (
            <Star key={i} fill="#FBBC04" />
          ))}
        </Flex>
      </Box>
    </Box>
  );
};

Stars.propTypes = { value: PropTypes.number.isRequired };

/*
 * The four-colour Google "G" logo glyph, shown unmodified next to the "Google
 * Maps" attribution text the Places terms require. Rendered at 16px (min 16dp).
 */
const GoogleLogo = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden focusable="false">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);

/**
 * Google rating badge for the cooperation landing page. Data is fetched at
 * build time via the Places API (see `GoogleRating` node in `gatsby-node.js`);
 * pass `rating`, `userRatingCount` and the `googleMapsUri` from the page query.
 * Renders nothing when no rating is available so the layout degrades cleanly.
 */
const GoogleRating = ({ rating, userRatingCount, googleMapsUri }) => {
  if (typeof rating !== 'number') return null;

  const ratingLabel = rating.toLocaleString('de-DE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const countLabel = (userRatingCount || 0).toLocaleString('de-DE');

  const inner = (
    <Box
      borderWidth="1px"
      borderStyle="solid"
      borderColor="blackAlpha.300"
      borderRadius="8px"
      px="12px"
      py="8px"
      bg="#fff"
      textAlign="center"
    >
      {/*
        Attribution required by the Places policies. The mark must read
        "Google Maps" (or the Google Maps logo); the text form is explicitly
        allowed where space is limited. `gap`/`mb` keep the logo's clear space.
      */}
      <Flex align="center" justify="center" gap="10px" mb="6px">
        <GoogleLogo />
        <Text fontSize="14px" fontWeight={700} color="gray.600" mb="0">
          Google Maps
        </Text>
      </Flex>
      <Flex align="center" justify="center" gap="6px">
        <Text
          fontSize="16px"
          fontWeight={700}
          color="gray.600"
          lineHeight="1"
          mb="0"
        >
          {ratingLabel}
        </Text>
        <Stars value={rating} />
        <Text fontSize="12px" color="gray.400" lineHeight="1" mb="0">
          ({countLabel})
        </Text>
      </Flex>
    </Box>
  );

  if (!googleMapsUri) return inner;

  return (
    <Box
      as="a"
      href={googleMapsUri}
      target="_blank"
      rel="noopener noreferrer"
      display="inline-block"
      _hover={{ textDecoration: 'none' }}
      aria-label={`Google Bewertung: ${ratingLabel} von 5 Sternen aus ${countLabel} Bewertungen`}
    >
      {inner}
    </Box>
  );
};

export default GoogleRating;

GoogleRating.propTypes = {
  rating: PropTypes.number,
  userRatingCount: PropTypes.number,
  googleMapsUri: PropTypes.string,
};

GoogleRating.defaultProps = {
  rating: undefined,
  userRatingCount: 0,
  googleMapsUri: null,
};
